import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RefreshToken } from '@utilities/refreshToken';
import { KYCActions, OnboardingFlowHandlerActions, selectKycApplication } from '@redux';
import { FarmerQuestionnaireActions } from '@redux/Services/FarmersQuestionnaire/actions';
import { storageHandler } from '@storageHandler';
import { useTranslation, Trans } from 'react-i18next';
import { history } from '@config';

const stepsOrder = [
  'farmRepresentativeSection',
  'farmerSection',
  'farmSection',
  'farmPartnersSection',
  'farmClientsAndSuppliersSection',
  'farmActivitiesSection',
  'farmPaymentsAndTransactionsSection'
];

const InitialFarmersSteps = [
  {
    title: 'farmers_fill_questionnaire',
    completed: false,
    icon: 'FarmersFillQuestionnaire',
    buttonTitle: 'farmers_confirm_questionnaire',
    buttonLink: '',
    stepsList: [],
    isCompleteButtonActive: false,
    isActive: true
  },
  {
    title: 'farmers_upload_documents',
    completed: false,
    icon: 'FarmersUploadDocuments',
    buttonTitle: 'farmers_upload_documents_button',
    buttonLink: '',
    isCompleteButtonActive: false,
    isActive: false
  },
  {
    title: 'farmers_wait_documents_approval',
    completed: false,
    icon: 'FarmersWaitDocumentsApproval',
    buttonTitle: 'representative_business_profile_verify_identity_button',
    buttonLink: '',
    isCompleteButtonActive: false,
    isActive: false
  },
  {
    title: 'farmers_sign_bank_agreement',
    completed: false,
    icon: 'FarmersSignBankAgreement',
    buttonTitle: 'representative_business_profile_update_profile_button',
    buttonLink: 'redirect_login',
    isCompleteButtonActive: false,
    isActive: false
  },
  {
    title: 'farmers_sign_into_bank',
    completed: false,
    icon: 'FarmersSignIntoBank',
    buttonTitle: 'representative_business_profile_update_profile_button',
    buttonLink: 'redirect_login',
    isCompleteButtonActive: false,
    isActive: false
  }
];

export const useFarmersQuestionnaire = () => {
  const dispatch = useDispatch();
  const application = useSelector(selectKycApplication);
  const { isCompletedModalVisible } = useSelector((state) => state.FarmersQuestionnaireReducer);
  const { isApplicationLoading } = useSelector((state) => state.KYCReducers);
  const questionnaireId = storageHandler.getSessionStorage('questionnaireId');
  const applicationId = storageHandler.getSessionStorage('applicationId');
  const { t } = useTranslation();

  const [result, setResult] = useState(null);
  const [farmerSteps, setFarmerSteps] = useState(InitialFarmersSteps);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState({});
  const [completedPage, setCompletedPage] = useState('');
  const [stepListReady, setStepListReady] = useState(false);
  const [farmerStepsReady, setFarmerStepsReady] = useState(false);
  const [uniqueSteps, setUniqueSteps] = useState([]);

  const getData = async () => {
    const result = await dispatch(FarmerQuestionnaireActions.getFarmerInformation(questionnaireId));
    setResult(result.payload);
  };

  const refreshToken = async () => {
    await dispatch(RefreshToken());
  };

  const updateSteps = async (data) => {
    if (!data) {
      setFarmerSteps(InitialFarmersSteps);
      return;
    }

    let appState = application?.applicationState;
    if (!appState) {
      const response = await dispatch(KYCActions.getApplication());
      appState = response?.payload?.applicationState;
    }

    setFarmerSteps((currentSteps) => {
      const uniqueSteps = createUniqueSteps(data);
      const reorderedUniqueSteps = stepsOrder
        .map((label) => uniqueSteps.find((step) => step.label === label))
        .filter(Boolean);

      setUniqueSteps(reorderedUniqueSteps);

      const updatedSteps = currentSteps.map((step, index) => {
        if (index === 0) {
          const allCompleted = reorderedUniqueSteps.every((step) => step.completed);
          return {
            ...step,
            stepsList: reorderedUniqueSteps,
            completed: allCompleted,
            isCompleteButtonActive: allCompleted && appState?.state !== 'UserUploadingDocuments'
          };
        }

        if (appState?.state === 'UserUploadingDocuments' && index === 1) {
          return {
            ...step,
            isActive: true,
            isCompleteButtonActive: true
          };
        }

        return step;
      });

      setStepListReady(true);
      setFarmerStepsReady(true);
      return updatedSteps;
    });
  };

  const createUniqueSteps = (data) => {
    return Object.keys(data).map((sectionKey) => {
      return {
        label: sectionKey,
        completed: data[sectionKey]?.isValid ?? false
      };
    });
  };

  const handleSuccessCallback = (succeededPage) => {
    setSuccessModalData(successModalDataByPage[succeededPage]);
    if (successModalDataByPage[succeededPage]) {
      setSuccessModalOpen(true);
    }
  };

  const questionnaireSubmitHandler = async () => {
    await dispatch(KYCActions.submitApplication(applicationId));
    await getData();
    const company = storageHandler.getSessionStorage('company');
    if (company) dispatch(OnboardingFlowHandlerActions.handleOnboardingFlow(company));
    handleModalClose();
  };

  const getNextStepLabel = () => {
    const completedIndex = uniqueSteps.findIndex((step) => step.label === completedPage);
    return uniqueSteps[completedIndex + 1]?.label ?? '';
  };

  const isNextStep = () => {
    const currentIndex = uniqueSteps.findIndex((step) => step.label === completedPage);
    return currentIndex < uniqueSteps.length - 1;
  };

  const successModalDataByPage = {
    questionnaireSuccessful: {
      title: t('farmers_questionnaire_success_modal_text'),
      button_text: t('farmers_questionnaire_success_modal_button'),
      isVisible: isCompletedModalVisible,
      submit: questionnaireSubmitHandler
    },
    completeNextSteps: {
      title: `${t(completedPage)} ${t('farmers_modal_info_provided')}`,
      subtitle: `${t('farmers_modal_next_section')}: ${t(getNextStepLabel())}`,
      button_text: `${t('farmers_modal_next_section')}: ${t(getNextStepLabel())}`,
      isVisible: true,
      submit: () => history.push(getNextStepLabel())
    },
    completePreviousSteps: {
      title: <Trans t={t} i18nKey="farmers_questionnaire_complete_previous_steps_modal_text" />,
      button_text: t('farmers_questionnaire_complete_previous_steps_modal_button'),
      isVisible: true,
      submit: () => handleModalClose()
    }
  };

  const handleModalClose = async () => {
    setSuccessModalOpen(false);
    if (successModalData?.title === 'farmers_questionnaire_success_modal_text') {
      await dispatch(FarmerQuestionnaireActions.completedModalVisible(false));
    }
  };

  const handleModalSubmit = () => {
    if (successModalData?.submit) successModalData.submit();
  };

  useEffect(() => {
    refreshToken();
    dispatch(KYCActions.getApplication());
    dispatch(FarmerQuestionnaireActions.resetQuestinnaireData());
    if (isCompletedModalVisible) dispatch(FarmerQuestionnaireActions.completedModalVisible(true));
  }, []);

  useEffect(() => {
    if (!result && questionnaireId) getData();
    if (result) updateSteps(result);
  }, [result, questionnaireId]);

  useEffect(() => {
    if (history.location.search.includes('success')) {
      const completedPage = new URLSearchParams(history.location.search).get('success');
      setCompletedPage(completedPage);
    }
  }, [history.location.search]);

  useEffect(() => {
    if (completedPage && !farmerSteps[0].completed && isNextStep()) {
      handleSuccessCallback('completeNextSteps');
    }
  }, [completedPage, uniqueSteps]);

  return {
    farmerSteps,
    application,
    loading: useSelector((state) => state.FarmersQuestionnaireReducer.loading),
    isApplicationLoading,
    stepListReady,
    farmerStepsReady,
    successModalOpen,
    successModalData,
    handleModalClose,
    handleModalSubmit,
    questionnaireSubmitHandler
  };
};
